import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  EmployerCandidateDirectoryOutreachDraftInput,
  EmployerCandidateDirectoryOutreachInput,
  EmployerCandidateDirectorySearchInput,
} from '@tabbio/types';
import { QUERY_KEYS } from '../lib/query-keys';
import {
  draftEmployerCandidateOutreach,
  fetchEmployerCandidateDetail,
  fetchEmployerCandidates,
  parseEmployerCandidateSearch,
  saveEmployerCandidate,
  sendEmployerCandidateOutreach,
} from '../lib/employer-candidates-api';
import { useWorkspace } from '../providers/useWorkspace';

function serializeEmployerCandidateParams(
  params: EmployerCandidateDirectorySearchInput = {},
) {
  return JSON.stringify({
    q: params.q ?? '',
    locations: params.locations ?? [],
    skills: params.skills ?? [],
    companies: params.companies ?? [],
    spotlight: params.spotlight ?? null,
    limit: params.limit ?? 24,
    jobId: params.jobId ?? null,
  });
}

function invalidateEmployerCandidateQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  companyId?: string | null,
) {
  queryClient.invalidateQueries({
    queryKey: ['employer-candidates', companyId ?? 'none'],
  });
  queryClient.invalidateQueries({
    queryKey: ['library', companyId ?? 'none'],
  });
}

export function useEmployerCandidates(
  params: EmployerCandidateDirectorySearchInput = {},
  enabled = true,
) {
  const { companyId } = useWorkspace();

  return useInfiniteQuery({
    queryKey: QUERY_KEYS.EMPLOYER_CANDIDATES(
      companyId,
      serializeEmployerCandidateParams(params),
    ),
    queryFn: ({ pageParam }) =>
      fetchEmployerCandidates({
        ...params,
        cursor: pageParam ?? undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: Boolean(companyId) && enabled,
    placeholderData: (previousData) => previousData,
    staleTime: 15_000,
  });
}

export function useEmployerCandidateDetail(
  candidateUserId: string,
  searchSessionId?: string | null,
  enabled = true,
) {
  const { companyId } = useWorkspace();

  return useQuery({
    queryKey: QUERY_KEYS.EMPLOYER_CANDIDATE_DETAIL(
      companyId,
      candidateUserId,
      searchSessionId,
    ),
    queryFn: () => fetchEmployerCandidateDetail(candidateUserId, searchSessionId),
    enabled: Boolean(companyId) && enabled && Boolean(candidateUserId),
    staleTime: 15_000,
  });
}

export function useSaveEmployerCandidate() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: (candidateUserId: string) => saveEmployerCandidate(candidateUserId),
    onSuccess: (_result, candidateUserId) => {
      invalidateEmployerCandidateQueries(queryClient, companyId);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EMPLOYER_CANDIDATE_DETAIL_SCOPE(
          companyId,
          candidateUserId,
        ),
      });
    },
  });
}

export function useParseEmployerCandidateSearch() {
  return useMutation({
    mutationFn: (query: string) => parseEmployerCandidateSearch(query),
  });
}

export function useDraftEmployerCandidateOutreach() {
  return useMutation({
    mutationFn: ({
      candidateUserId,
      input,
    }: {
      candidateUserId: string;
      input?: EmployerCandidateDirectoryOutreachDraftInput;
    }) => draftEmployerCandidateOutreach(candidateUserId, input),
  });
}

export function useSendEmployerCandidateOutreach() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: ({
      candidateUserId,
      input,
    }: {
      candidateUserId: string;
      input: EmployerCandidateDirectoryOutreachInput;
    }) => sendEmployerCandidateOutreach(candidateUserId, input),
    onSuccess: (_result, variables) => {
      invalidateEmployerCandidateQueries(queryClient, companyId);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EMPLOYER_CANDIDATE_DETAIL_SCOPE(
          companyId,
          variables.candidateUserId,
        ),
      });
    },
  });
}
